import React, { useMemo } from 'react';
import loadable from '@loadable/component';
import { Message } from '@naf/input';
import { LocationSlugs } from '@appTypes/nafLocations';
import { useGetNafLocations } from '@hooks/useGetNafLocations';
import { Spinner } from '@components/spinner/Spinner';
import { NafLocationsMapBlockType } from '@appTypes/NafLocationsMapBlockType';
import carPOIIcon from '@assets/images/carPOI.svg?url';
import centerInactiveIcon from '@assets/images/center-inactive.svg?url';
import centerActiveIcon from '@assets/images/center-active.svg?url';
import { EMPTY_COUNTIES, sourceToSlug, createLocationUrl } from './NafLocationsMapBlock.utils';
import * as S from './NafLocationsMapBlock.styles';

const GoogleMaps = loadable(() => import('@pages/NafLocationsApp/components/GoogleMap'));

export const NafLocationsMapBlock = ({ block }: { block: NafLocationsMapBlockType }) => {
  const locationSlug = sourceToSlug(block);
  const { nafLocationsByCounty, fetchState, error } = useGetNafLocations(locationSlug, EMPTY_COUNTIES, 1000);

  const markers = useMemo(
    () =>
      nafLocationsByCounty.flatMap(({ nafLocations }) =>
        nafLocations
          .filter((l) => l.coordinates !== null)
          .map((location) => ({
            lat: location.coordinates.latitude,
            lng: location.coordinates.longitude,
            title: location.title,
            ingress: location.ingress,
            link: createLocationUrl(locationSlug, location.link.data.slug),
          })),
      ),
    [locationSlug, nafLocationsByCounty],
  );

  const isPracticeTracks = locationSlug === LocationSlugs.practiceTracks;

  if (fetchState === 'initial' || (fetchState === 'isUpdating' && nafLocationsByCounty.length === 0)) {
    return (
      <S.MapContainer>
        <Spinner />
      </S.MapContainer>
    );
  }

  if (error) {
    return <Message error>{error.message}</Message>;
  }

  if (markers.length === 0) {
    return <Message>Klarte ikke finne steder å vise i kartet.</Message>;
  }

  return (
    <GoogleMaps
      markers={markers}
      selectedLabel={isPracticeTracks ? 'Valgt øvingsbane' : 'Valgt senter'}
      inactiveIcon={isPracticeTracks ? carPOIIcon : centerInactiveIcon}
      activeIconOverride={isPracticeTracks ? carPOIIcon : centerActiveIcon}
    />
  );
};
